import type { FormDataAdapter } from "$lib/util"
import {
  adaptBeneficiariesFormData,
  ONE_HUNDRED,
} from "$lib/util/account-beneficiary"
import {
  ACCOUNT_BENEFICIARY_TYPES,
  TAX_NUMBER_TYPES,
  ValidateYup,
  type AccountBeneficiary,
  type AccountOpeningApplication,
} from "@tastyworks/tastyworks-api"
import {
  array,
  boolean,
  mixed,
  object,
  string,
  type InferType,
  type TestConfig,
} from "yup"
import { addressAdapter, addressSchema } from "../address"

export enum ADD_BENEFICIARY_CHOICES {
  INDIVIDUAL = "individual",
  TRUST = "trust",
  SKIP = "skip",
}

export const ADD_BENEFICIARY_CHOICE_VALUES = Object.values(
  ADD_BENEFICIARY_CHOICES
)

const beneficiaryPersonalInfoSchema = object({
  taxNumberType: mixed<TAX_NUMBER_TYPES>()
    .oneOf(Object.values(TAX_NUMBER_TYPES))
    .defined(),
  taxNumber: string().required(),
})

export const accountBeneficiarySchema = ValidateYup.accountBeneficiarySchema
  .concat(
    object({
      address: addressSchema,
      beneficiaryType: mixed<ACCOUNT_BENEFICIARY_TYPES>()
        .oneOf(Object.values(ACCOUNT_BENEFICIARY_TYPES))
        .required(),
    })
  )
  .concat(beneficiaryPersonalInfoSchema)

export type AccountBeneficiaryFormData = InferType<
  typeof accountBeneficiarySchema
>

const testOneHundredPercentConfig: TestConfig<
  AccountBeneficiaryFormData[] | undefined
> = {
  name: "exactly-100%",
  message: "yup.common.number.exactlyOneHundredPercent",
  test: (value) => {
    if (!value || value.length === 0) {
      return true
    }

    let total = 0.0
    for (const applicant of value) {
      if (applicant.beneficiaryPercent === undefined) continue
      total += applicant.beneficiaryPercent
    }

    return total === ONE_HUNDRED
  },
}

export const beneficiaryNameSchema = accountBeneficiarySchema.pick([
  "firstName",
  "middleName",
  "lastName",
])

export const beneficiaryDetailsSchema = ValidateYup.accountBeneficiarySchema
  .pick(["beneficiaryRelationship", "beneficiaryPercent", "birthDate"])
  .concat(beneficiaryPersonalInfoSchema)

export const beneficiaryAddressSchema = object({
  address: addressSchema,
})

const beneficiaryArrayWithoutPercentValidation = array()
  .of(accountBeneficiarySchema)
  .ensure()

export const typeOfBeneficiarySchema = object({
  typeOfBeneficiary: mixed<ADD_BENEFICIARY_CHOICES>()
    .oneOf(ADD_BENEFICIARY_CHOICE_VALUES)
    .required(),
})

export const beneficiariesSchema = object({
  shouldShowBeneficiaryForm: boolean().default(false),
  primary: beneficiaryArrayWithoutPercentValidation
    .clone()
    .required()
    .when(
      "shouldShowBeneficiaryForm",
      ([shouldShowBeneficiaryForm], schema) => {
        return shouldShowBeneficiaryForm
          ? schema.notRequired()
          : schema
              .min(1, "yup.primaryBeneficiary.minimum.atLeastOne")
              .test(testOneHundredPercentConfig)
      }
    ),
  contingent: beneficiaryArrayWithoutPercentValidation
    .clone()
    .required()
    .when(
      "shouldShowBeneficiaryForm",
      ([shouldShowBeneficiaryForm], schema) => {
        return shouldShowBeneficiaryForm
          ? schema
          : schema.test(testOneHundredPercentConfig)
      }
    ),
})

export const beneficiarySchema = object({
  typeOfBeneficiary: typeOfBeneficiarySchema,
  beneficiaries: beneficiariesSchema,
  beneficiaryName: beneficiaryNameSchema,
  beneficiaryDetails: beneficiaryDetailsSchema,
  beneficiaryAddress: beneficiaryAddressSchema,
  trustBeneficiary: object({}), // Empty object required for form step validation
})

export const beneficiariesAdapter = {
  toForm: (application, formData) => {
    const toFormData = (beneficiaryType: ACCOUNT_BENEFICIARY_TYPES) =>
      application.accountBeneficiaries
        .filter(
          (beneficiary) => beneficiary.beneficiaryType === beneficiaryType
        )
        .map(
          (beneficiary) =>
            accountBeneficiaryAdapter.toForm(
              beneficiary,
              {}
            ) as AccountBeneficiaryFormData
        )

    formData.primary = toFormData(ACCOUNT_BENEFICIARY_TYPES.PRIMARY)
    formData.contingent = toFormData(ACCOUNT_BENEFICIARY_TYPES.CONTINGENT)
    return formData
  },
  toPatch: (data) => ({
    accountBeneficiaries: adaptBeneficiariesFormData(data),
  }),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof beneficiariesSchema>
  >,
  "toForm" | "toPatch"
>

export const accountBeneficiaryAdapter: FormDataAdapter<
  AccountBeneficiary,
  AccountBeneficiaryFormData
> = {
  toForm(accountBeneficiary, formData) {
    const address = (accountBeneficiary as AccountBeneficiary).address

    formData.firstName = accountBeneficiary.firstName
    formData.middleName = accountBeneficiary.middleName
    formData.lastName = accountBeneficiary.lastName

    formData.birthDate = accountBeneficiary.birthDate || undefined

    formData.taxNumberType = accountBeneficiary.taxNumberType
    formData.taxNumber = accountBeneficiary.taxNumber

    formData.beneficiaryType = accountBeneficiary.beneficiaryType
    formData.beneficiaryRelationship =
      accountBeneficiary.beneficiaryRelationship
    formData.beneficiaryPercent = accountBeneficiary.beneficiaryPercent

    formData.address = { ...address }

    return formData
  },
  toModel(formData, accountBeneficiary) {
    accountBeneficiary.firstName = formData.firstName
    accountBeneficiary.middleName = formData.middleName || ""
    accountBeneficiary.lastName = formData.lastName

    accountBeneficiary.birthDate = formData.birthDate
    accountBeneficiary.taxNumberType = formData.taxNumberType
    accountBeneficiary.taxNumber = formData.taxNumber

    accountBeneficiary.beneficiaryType = formData.beneficiaryType
    accountBeneficiary.beneficiaryRelationship =
      formData.beneficiaryRelationship
    accountBeneficiary.beneficiaryPercent = formData.beneficiaryPercent || 0

    accountBeneficiary.address = addressAdapter.toModel(
      formData.address,
      accountBeneficiary.address
    )

    return accountBeneficiary
  },
}
